import React from 'react';
import { ExternalLink } from '../router';
import { BOOKING_URL } from '../links';
import SquareOfHealth from '../components/SquareOfHealth';

const credentials = [
  ['Registered Sports Kinesiologist (Hyperton-X)', 'International Institute for Total Mind-Body Integration', '2009–Present'],
  ['Certified NLP Practitioner', 'NLP School London, dir. Robbie Steinhouse', 'Certified'],
  ['CranioSacral Therapy Certification', 'The Upledger Institute', 'Certified'],
  ['MRI Interpretation Certification', 'Medical University of Moscow', 'Certified'],
  ['Blood Work Interpretation', 'Medical University of Moscow', 'Certified'],
  ['Personal Health Coaching Certification', 'Institute of Applied Kinesiology', 'Certified'],
  ['Registered Herbalist / Medical Herbalist', 'Instytut Zielarstwa Polskiego i Terapii Naturalnych, Warsaw', 'Certified'],
  ['Physiotherapy (PT)', '—', 'Certified'],
  ['Tibetan Medicine', '—', 'Certified'],
];

// TODO(Hanna): confirm the Women Empowered video and send the Compelling Communicators link.
const featured = [
  ['What Is Applied Kinesiology?', 'https://www.youtube.com/watch?v=4270T6K0L1g'],
  ['Charla Anderson Podcast — Your Brain Is Not Broken', 'https://www.youtube.com/watch?v=-pDoDOOZUsA'],
  ['Health, Wellness & Travel Podcast', 'https://www.youtube.com/watch?v=JvndqQNu9LU'],
  ['Compelling Communicators — with Chris Hanlon', ''],
];

const About: React.FC = () => (
  <>
    <section className="section page-hero">
      <div className="container container--narrow center">
        <h1>I Had to Rebuild Myself First</h1>
        <p className="subtitle">
          The method I use with technical professionals started as the only way out of my own collapse.
        </p>
      </div>
    </section>

    <section className="section">
      <div className="container about-me">
        <div className="about-me__text">
        <h2>About Me</h2>
        <p>
          In my early 20s, five concussions from martial arts left me unable to get out of bed without pain shooting through my
          body. I would stare at the same sentence for an hour, reading it over and over, unable to hold a thought together. The
          pain frightened me. The fog was worse.
        </p>
        <p className="lead">
          The question that kept circling was darker: <em>what if this broken version of me is permanent?</em>
        </p>
        <p>
          Doctors told me I would live with it. <strong>I refused to accept that.</strong> I became my own test subject, diving into Tibetan
          medicine, applied kinesiology, CranioSacral therapy, medical herbalism, NLP, and somatics, looking for what treated my
          body as <strong>one connected system</strong> instead of a list of separate symptoms.
        </p>
        <p>
          Slowly, the fog lifted. The pain became rare, then gone. My mind came back online,{' '}
          <strong>sharper than before the injuries.</strong>
        </p>
        <p>
          More than twenty years later I am still training in martial arts, and I have never stopped refining what brought me
          back. That method is what I now use with other people.
        </p>
        <p>
          I work with engineers, tech leads, and senior technical professionals who are exactly where I was: sharp minds running
          on bodies that have quietly stopped keeping up. Over <strong>15 years</strong> I have guided <strong>1,000+</strong> of
          them across <strong>12 countries</strong>, and reached another <strong>10,000+</strong> through talks and workshops.
        </p>
        </div>
        <div className="about-me__photo">
          <img src="/aa8b4378.jpg" alt="Hanna Kinez" />
        </div>
      </div>
      <div className="container">
        <figure className="wide-photo">
          <img src="/workshop2.jpg" alt="Hanna leading a workshop for a room full of tech professionals" />
        </figure>
      </div>
    </section>

    <section className="section section--alt">
      <div className="container container--narrow">
        <h2>The KinezioSomatic Method &amp; the Square of Health</h2>
        <p>
          I am the founder of the <strong>KinezioSomatic method</strong>. It is built on what I call the{' '}
          <strong>Square of Health</strong>: four dimensions that together drive how you feel, think, and perform:
        </p>
        <SquareOfHealth />
        <ul className="list">
          <li>
            <strong>Physical:</strong> posture, movement, biomechanics, sleep, training load.
          </li>
          <li>
            <strong>Biochemical:</strong> nutrition, hydration, minerals, herbs, lab markers.
          </li>
          <li>
            <strong>Energy:</strong> the body's energy system: Chinese meridians and the five-element (Wu Xing) model, and your
            autonomic nervous system.
          </li>
          <li>
            <strong>Mental &amp; Psychosocial:</strong> psychology, stress, workload, and life context, where NLP reframing,
            leadership and success coaching come in.
          </li>
        </ul>
        <p>
          Most approaches fix one corner and ignore the other three. I read all four, find what is failing, and rebuild from there.
          Then I hand you a system you can run yourself.
        </p>
      </div>
    </section>

    <section className="section" id="credentials">
      <div className="container container--narrow">
        <h2>Credentials</h2>
        <div className="table-wrap">
          <table className="credentials-table">
            <thead>
              <tr>
                <th>Credential</th>
                <th>Institution</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map(([credential, institution, status]) => (
                <tr key={credential}>
                  <td>{credential}</td>
                  <td>{institution}</td>
                  <td>{status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          <strong>Still learning:</strong> the body never stops teaching me. I regularly attend workshops and training, because
          what worked five years ago might not be what you need today.
        </p>
      </div>
    </section>

    <section className="section section--alt">
      <div className="container container--narrow">
        <h2>Featured In</h2>
        <ul className="link-list link-list--large">
          {featured.map(([label, href]) => (
            <li key={label}>{href ? <ExternalLink href={href}>{label}</ExternalLink> : label}</li>
          ))}
        </ul>
        <ExternalLink href={BOOKING_URL} className="btn btn-primary">
          Book a Complimentary Performance Strategy Session
        </ExternalLink>
      </div>
    </section>
  </>
);

export default About;
